"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __createBinding = (this && this.__createBinding) || (Object.create ? (function (o, m, k, k2) {
    if (k2 === undefined)
        k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
        desc = { enumerable: true, get: function () { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function (o, m, k, k2) {
    if (k2 === undefined)
        k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function (o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function (o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function (o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o)
                if (Object.prototype.hasOwnProperty.call(o, k))
                    ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule)
            return mod;
        var result = {};
        if (mod != null)
            for (var k = ownKeys(mod), i = 0; i < k.length; i++)
                if (k[i] !== "default")
                    __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollaboratorFilterControl = void 0;
const react_1 = __importStar(require("react"));
const ui_components_1 = require("@groupby/ui-components");
/**
 * CollaboratorFilterControl
 *
 * A wrapper around ListFilterControl that translates between collaborator IDs
 * (used internally for unique React keys and selection tracking) and emails/names
 * (stored in the filter model and sent to the API).
 *
 * This solves the problem where the API returns multiple collaborator entries with
 * the same email but different IDs, causing duplicate React keys and broken
 * multi-selection when email is used as the option value.
 */
exports.CollaboratorFilterControl = (0, react_1.forwardRef)((_a, ref) => {
    var { loadOptions: originalLoadOptions, item, applyValue } = _a, restProps = __rest(_a, ["loadOptions", "item", "applyValue"]);
    const listFilterRef = (0, react_1.useRef)(null);
    const idToNameRef = (0, react_1.useRef)(new Map());
    const nameToIdsRef = (0, react_1.useRef)(new Map());
    const [isMapReady, setIsMapReady] = (0, react_1.useState)(false);
    const translateIdsToEmails = (0, react_1.useCallback)((values) => [...new Set(values.map((id) => { var _a; return (_a = idToNameRef.current.get(String(id))) !== null && _a !== void 0 ? _a : String(id); }))], []);
    /**
     * Wraps the original loadOptions to capture the id↔email mappings
     * while returning options with id as value (for unique React keys).
     */
    const loadOptionsWithMap = (0, react_1.useCallback)((...args) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        const options = (_a = yield (originalLoadOptions === null || originalLoadOptions === void 0 ? void 0 : originalLoadOptions(...args))) !== null && _a !== void 0 ? _a : [];
        const idToName = new Map();
        const nameToIds = new Map();
        options.forEach(({ value, label }) => {
            var _a;
            const id = String(value);
            const name = String(label);
            idToName.set(id, name);
            nameToIds.set(name, [...((_a = nameToIds.get(name)) !== null && _a !== void 0 ? _a : []), id]);
        });
        idToNameRef.current = idToName;
        nameToIdsRef.current = nameToIds;
        setIsMapReady(true);
        return options;
    }), [originalLoadOptions]);
    /**
     * Translates incoming filter model values (emails) to internal IDs
     * so that checkboxes match the id-based option values.
     */
    const translatedItem = (0, react_1.useMemo)(() => {
        if (!isMapReady || !(item === null || item === void 0 ? void 0 : item.value) || !Array.isArray(item.value))
            return item;
        const translatedValue = item.value.flatMap((email) => { var _a; return (_a = nameToIdsRef.current.get(String(email))) !== null && _a !== void 0 ? _a : [email]; });
        return Object.assign(Object.assign({}, item), { value: translatedValue });
    }, [isMapReady, item]);
    /**
     * Translates outgoing selected IDs back to emails for the filter model.
     * Deduplicates emails since multiple IDs can map to the same email.
     */
    const handleApplyValue = (0, react_1.useCallback)((filterItem) => {
        if (!applyValue)
            return;
        const values = filterItem.value;
        if (!Array.isArray(values)) {
            applyValue(filterItem);
            return;
        }
        const translatedEmails = translateIdsToEmails(values);
        applyValue(Object.assign(Object.assign({}, filterItem), { value: translatedEmails }));
    }, [applyValue, translateIdsToEmails]);
    (0, react_1.useImperativeHandle)(ref, () => ({
        getValue: () => {
            var _a, _b, _c;
            const filterItem = (_a = listFilterRef.current) === null || _a === void 0 ? void 0 : _a.getValue();
            if (!filterItem) {
                return {
                    id: 0,
                    field: (_b = item === null || item === void 0 ? void 0 : item.field) !== null && _b !== void 0 ? _b : '',
                    operator: (_c = item === null || item === void 0 ? void 0 : item.operator) !== null && _c !== void 0 ? _c : '',
                    value: [],
                };
            }
            const values = filterItem.value;
            if (!Array.isArray(values)) {
                return filterItem;
            }
            return Object.assign(Object.assign({}, filterItem), { value: translateIdsToEmails(values) });
        },
        validate: () => { var _a, _b; return (_b = (_a = listFilterRef.current) === null || _a === void 0 ? void 0 : _a.validate()) !== null && _b !== void 0 ? _b : true; },
        clear: () => { var _a; return (_a = listFilterRef.current) === null || _a === void 0 ? void 0 : _a.clear(); },
        setValue: (value) => {
            var _a, _b;
            if (!Array.isArray(value)) {
                (_a = listFilterRef.current) === null || _a === void 0 ? void 0 : _a.setValue(value);
                return;
            }
            const translatedIds = value.flatMap((email) => { var _a; return (_a = nameToIdsRef.current.get(String(email))) !== null && _a !== void 0 ? _a : [email]; });
            (_b = listFilterRef.current) === null || _b === void 0 ? void 0 : _b.setValue(translatedIds);
        },
    }), [item === null || item === void 0 ? void 0 : item.field, item === null || item === void 0 ? void 0 : item.operator, translateIdsToEmails]);
    return (react_1.default.createElement(ui_components_1.ListFilterControl, Object.assign(Object.assign({ ref: listFilterRef }, restProps), { item: translatedItem, loadOptions: loadOptionsWithMap, applyValue: handleApplyValue })));
});
exports.CollaboratorFilterControl.displayName = 'CollaboratorFilterControl';
